import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { useAuthBootstrap } from '@/shared/lib';

import {
  fetchBillingCoverage,
  fetchBillingHistory,
  fetchBillingOverview,
  fetchBillingPublicPlans,
  fetchBillingSupportRequestPlans,
  fetchBillingUsage,
} from './billing-api';
import { isBillingHttpConfigured } from './billing-http-client';
import { isBillingUiDisabledError } from './standard-billing-payload';
import type {
  BillingCoverageResponse,
  BillingHistoryResponse,
  BillingOverviewResponse,
  BillingPublicPlansResponse,
  BillingUsageResponse,
} from './types';

const staleTime = 60_000;

export const billingQueryKeys = {
  root: () => ['billing'] as const,
  overview: () => [...billingQueryKeys.root(), 'overview'] as const,
  usage: () => [...billingQueryKeys.root(), 'usage'] as const,
  history: () => [...billingQueryKeys.root(), 'history'] as const,
  coverage: () => [...billingQueryKeys.root(), 'coverage'] as const,
  publicPlans: () => [...billingQueryKeys.root(), 'plans', 'public'] as const,
  supportRequestPlans: () => [...billingQueryKeys.root(), 'plans', 'support-request'] as const,
};

export const useBillingOverviewQuery = (
  options?: Omit<UseQueryOptions<BillingOverviewResponse>, 'queryKey' | 'queryFn'>,
) => {
  const { hasToken } = useAuthBootstrap();
  const configured = isBillingHttpConfigured();
  const { enabled: enabledOption, ...rest } = options ?? {};

  return useQuery({
    ...rest,
    queryKey: billingQueryKeys.overview(),
    queryFn: fetchBillingOverview,
    enabled: Boolean(hasToken && configured && (enabledOption ?? true)),
    staleTime,
  });
};

export const useBillingUsageQuery = (
  options?: Omit<UseQueryOptions<BillingUsageResponse>, 'queryKey' | 'queryFn'>,
) => {
  const { hasToken } = useAuthBootstrap();
  const configured = isBillingHttpConfigured();
  const { enabled: enabledOption, ...rest } = options ?? {};

  return useQuery({
    ...rest,
    queryKey: billingQueryKeys.usage(),
    queryFn: fetchBillingUsage,
    enabled: Boolean(hasToken && configured && (enabledOption ?? true)),
    staleTime,
  });
};

export const useBillingHistoryQuery = (
  options?: Omit<UseQueryOptions<BillingHistoryResponse>, 'queryKey' | 'queryFn'>,
) => {
  const { hasToken } = useAuthBootstrap();
  const configured = isBillingHttpConfigured();
  const { enabled: enabledOption, ...rest } = options ?? {};

  return useQuery({
    ...rest,
    queryKey: billingQueryKeys.history(),
    queryFn: fetchBillingHistory,
    enabled: Boolean(hasToken && configured && (enabledOption ?? true)),
    staleTime,
  });
};

export const useBillingCoverageQuery = (
  options?: Omit<UseQueryOptions<BillingCoverageResponse>, 'queryKey' | 'queryFn'>,
) => {
  const { hasToken } = useAuthBootstrap();
  const configured = isBillingHttpConfigured();
  const { enabled: enabledOption, ...rest } = options ?? {};

  return useQuery({
    ...rest,
    queryKey: billingQueryKeys.coverage(),
    queryFn: fetchBillingCoverage,
    enabled: Boolean(hasToken && configured && (enabledOption ?? true)),
    staleTime,
  });
};

export const useBillingPublicPlansQuery = (
  options?: Omit<UseQueryOptions<BillingPublicPlansResponse>, 'queryKey' | 'queryFn'>,
) => {
  const { hasToken } = useAuthBootstrap();
  const configured = isBillingHttpConfigured();
  const { enabled: enabledOption, retry: userRetry, ...rest } = options ?? {};

  return useQuery({
    ...rest,
    queryKey: billingQueryKeys.publicPlans(),
    queryFn: fetchBillingPublicPlans,
    enabled: Boolean(hasToken && configured && (enabledOption ?? true)),
    staleTime,
    retry: (failureCount, error) => {
      if (isBillingUiDisabledError(error)) return false;
      if (userRetry === false) return false;
      if (typeof userRetry === 'number') return failureCount < userRetry;
      if (typeof userRetry === 'function') return userRetry(failureCount, error as Error);
      return failureCount < 3;
    },
  });
};

export const useBillingSupportRequestPlansQuery = (
  options?: Omit<UseQueryOptions<BillingPublicPlansResponse>, 'queryKey' | 'queryFn'>,
) => {
  const { hasToken } = useAuthBootstrap();
  const configured = isBillingHttpConfigured();
  const { enabled: enabledOption, retry: userRetry, ...rest } = options ?? {};

  return useQuery({
    ...rest,
    queryKey: billingQueryKeys.supportRequestPlans(),
    queryFn: fetchBillingSupportRequestPlans,
    enabled: Boolean(hasToken && configured && (enabledOption ?? true)),
    staleTime,
    retry: (failureCount, error) => {
      if (isBillingUiDisabledError(error)) return false;
      if (userRetry === false) return false;
      if (typeof userRetry === 'number') return failureCount < userRetry;
      if (typeof userRetry === 'function') return userRetry(failureCount, error as Error);
      return failureCount < 3;
    },
  });
};
